import { inject, Injectable } from '@angular/core';
import { BaseAppService } from '../base-app-service';
import { HttpParams } from '@angular/common/http';
import { CurrentService } from '../../current.service';
import { CustomerService } from '../../customer.service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/kitchen/configuration';
import { KitchenPricing } from 'src/app/models/configurators-2d/applications/kitchen/kitchen-configuration-price';
import { KitchenConfigurationData } from '../../../stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { Subscription } from 'rxjs';
import { Customer } from '../../kingfisher/customer';
import { ProjectService } from '../../project.service';
import { Funding } from '../../../stores/tunnel/model/funding';
import { FundingState } from '../../../stores/tunnel/model/funding-state';
import { InstallationState } from '../../../stores/tunnel/model/installation-state';
import { KitchenExtraPricing } from '../../../stores/tunnel/model/kitchen-extra-pricing';
import { Price } from '../../../stores/tunnel/model/price';
import { PricingState } from '../../../stores/tunnel/model/pricing-state';
import { toInstallationPriceRange } from '../../helpers/installation-price-range.helper';
import { DiscountTypeEnum } from '../../../enum/discount-type.enum';
import { WorkbenchService } from './workbench.service';
import { WorkbenchEan } from '../../../models/configurators-2d/applications/workbench/workbench-ean';
import { InstallationInput } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { GetCustomerFidelityInfoNouveauPointInput } from '../../fidelity/fidelity';
import { FidelityService } from '../../fidelity/fidelity.service';
import { AddWorkbenchInput } from '../../../models/configurators-2d/tunnel/kitchen/add-workbench-input';
import { BaseKitchenConfigurationDto } from '../../../models/configurators-2d/tunnel/kitchen/base-kitchen-configuration-dto';
import { KitchenConfiguration } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-configuration';
import { KitchenCreateUpdateTunnel } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-create-update-tunnel';
import { KitchenInspiration } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-inspiration';
import { KitchenInspirationDto } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-inspiration-dto';
import { KitchenInstallationDto } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-installation-dto';
import { KitchenTunnelConceptionDetailPrice } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-conception-detail-price';
import { KitchenTunnelPricingDto } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-pricing-dto';
import { KitchenTunnelWorkbenchPrice } from '../../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-workbench-price';
import { UpdateInspirationInput } from '../../../models/configurators-2d/tunnel/kitchen/update-inspiration-input';
import {
  DuplicateTunnelHBMInput,
  GetTunnelByHBMUUIDInput,
  HbmPdfViewModel,
} from '../../../models/configurators-2d/tunnel/common-hbm-tunnel-dtos';
import { ApplyDiscountInput } from '../../../models/configurators-2d/tunnel/apply-discount-input';
import { BaseTunnelFundingDto } from '../../../models/configurators-2d/tunnel/base-tunnel-funding-dto';
import { DeleteTunnelInput } from '../../../models/configurators-2d/tunnel/delete-tunnel-input';
import { FundingLegalInformation } from '../../../models/configurators-2d/tunnel/funding-legal-information';
import { GetComplementaryProductsInput } from '../../../models/configurators-2d/tunnel/get-complementary-products-input';
import { GetFundingDataInput } from '../../../models/configurators-2d/tunnel/get-funding-data-input';
import { GetFundingLegalInformationInput } from '../../../models/configurators-2d/tunnel/get-funding-legal-information-input';
import { GetInspirationInput } from '../../../models/configurators-2d/tunnel/get-inspiration-input';
import { GetInstallationDataInput } from '../../../models/configurators-2d/tunnel/get-installation-data-input';
import { GetPricingInput } from '../../../models/configurators-2d/tunnel/get-pricing-input';
import { TunnelComplementaryProduct } from '../../../models/configurators-2d/tunnel/tunnel-complementary-product';
import { TunnelUpdateInstallation } from '../../../models/configurators-2d/tunnel/tunnel-update-installation';
import { UpdateInstallationInput } from '../../../models/configurators-2d/tunnel/update-installation-input';
import { HBMMedia, HBMMedias } from '../../../models/configurators-2d/applications/kitchen/hbm-media';
import { logError } from '../../helpers/sentry.helper';
import { ComplementaryProduct } from '../../../models/configurators-2d/complementary-products/complementary-product';
import { ComplementaryProductOrderService } from '../../complementary-products/complementary-product-order.service';
import { FeatureSettingsService } from '../../feature-settings/feature-settings.service';
import { BomDataHelper } from '../../helpers/bom-data-helper';
import { FundingRehydrationService } from '../funding-rehydration.service';

@Injectable({
  providedIn: 'root',
})
export class KitchenService extends BaseAppService<Configuration> {
  private readonly fundingRehydration = inject(FundingRehydrationService);
  private currentKitchenConfiguration: KitchenConfigurationData;
  private currentCustomer: Customer;
  private currentInstallation: InstallationState;
  private currentDiscount: DiscountTypeEnum;
  private currentFunding: FundingState;
  private currentPricing: PricingState;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    protected current: CurrentService,
    protected customer: CustomerService,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly projectService: ProjectService,
    private readonly workbenchService: WorkbenchService,
    private readonly fidelityService: FidelityService,
    private readonly featureSettingsService: FeatureSettingsService,
    private readonly complementaryProductOrderService: ComplementaryProductOrderService
  ) {
    super(CONFIG_CONTEXT.KITCHEN, Configuration);

    this.subscribeToObservable();
  }

  private subscribeToObservable() {
    this.subscriptions.push(
      this.tunnelDataStore.getConfigurationData().subscribe(async configurationData => {
        if (configurationData) {
          this.currentKitchenConfiguration = configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;
        }
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer.userData;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(async installation => {
        if (installation) {
          this.currentInstallation = installation;
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(async discount => {
        if (discount) {
          this.currentDiscount = discount;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(funding => {
        if (funding) {
          this.currentFunding = funding;
        }
      }),
      this.tunnelDataStore.getCurrentPricing().subscribe(pricing => {
        if (pricing) {
          this.currentPricing = pricing;
        }
      })
    );
  }

  /**
   * Consentement déjà enregistré pour ce projet, si connu. Lit d'abord l'état du tunnel Kitchen
   * ({@link currentKitchenConfiguration}, jamais alimenté par {@link BaseAppService.currentConfiguration}
   * pour ce flux HBM) ; si ce projet vient d'être rouvert et que la valeur n'a pas encore été
   * chargée en mémoire cette session, va la chercher en base et la met en cache.
   */
  protected override async getPersistedContactedByPhone(configId: string): Promise<boolean | undefined> {
    if (this.currentKitchenConfiguration?.kitchenConfigurationId !== configId) {
      return undefined;
    }
    if (this.currentKitchenConfiguration.contactedByPhone !== undefined) {
      return this.currentKitchenConfiguration.contactedByPhone;
    }

    const persisted = await this.fetchContactedByPhone(configId);
    this.currentKitchenConfiguration.contactedByPhone = persisted;
    return persisted;
  }

  /**
   * Mémorise le consentement résolu dans l'état du tunnel Kitchen, pour qu'un prochain envoi
   * dans la même session (ex. après déverrouillage) le réutilise au lieu de rouvrir la pop-up.
   */
  protected override setPersistedContactedByPhone(configId: string, contactedByPhone: boolean): void {
    if (this.currentKitchenConfiguration?.kitchenConfigurationId === configId) {
      this.currentKitchenConfiguration.contactedByPhone = contactedByPhone;
    }
  }

  protected override get currentConfigurationIdForContactedByPhone(): string | undefined {
    return this.currentKitchenConfiguration?.kitchenConfigurationId;
  }

  public async getLiteKitchenConfiguration(HBMConfigurationId: string): Promise<BaseKitchenConfigurationDto> {
    return await this.getKitchenTunnelByUUID({
      hbmUUID: HBMConfigurationId,
    });
  }

  public async loadKitchenConfigurationFromHBM(HBMConfigurationId: string): Promise<BaseKitchenConfigurationDto> {
    //le GUID est sensible à la casse, il faut le mettre en majuscule
    HBMConfigurationId = HBMConfigurationId.toUpperCase();
    let result;
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.KITCHEN, {
      configurationId: null,
      kitchenConfigurationId: null,
      workbenchConfigurationId: null,
      projectTitle: null,
      bom: null,
      inspiration: null,

      medias: await this.getKitchenMedias(HBMConfigurationId),
      HBMConfigurationId: HBMConfigurationId,
    } as KitchenConfigurationData);

    try {
      result = await this.getKitchenTunnelByUUID({ hbmUUID: HBMConfigurationId });

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.KITCHEN, {
        configurationId: result?.id,
        kitchenConfigurationId: result?.kitchenConfigurationId,
        workbenchConfigurationId: result?.workbenchConfigurationId,
        HBMConfigurationId: HBMConfigurationId,
        projectTitle: result?.title,
        bom: result?.bom,
        medias: await this.getKitchenMedias(HBMConfigurationId),
        inspiration: await this.initKitchenInspiration(result?.id),
      } as KitchenConfigurationData);
      if (result?.workbenchConfigurationId) {
        await this.loadWorkbenchConfiguration(result.workbenchConfigurationId);
      }
    } catch { }

    //Si le projet qu'on charge n'a pas de BOM, c'est qu'il n'a pas passé l'étape de choix des quantités. C'est un projet uniquement créé dans HBM et enregistré.
    //On ne charge donc pas les données
    if (result != null && !BomDataHelper.isBomUnavailableForProductList(result?.bom)) {
      await this.loadKitchenPricing(result.id);
      // Filet : `Tunnel/pricing` ne ramène pas toujours les offres persistées.
      await this.fundingRehydration.rehydrateIfNeeded(result.id, this);
      await this.computeFidelity();
      await this.computeComplementaryProduct({ tunnelConfigurationId: result.id }, false);
    }

    return result;
  }

  private async loadKitchenFunding(input: KitchenTunnelPricingDto) {
    this.tunnelDataStore.updateCurrentFunding({
      ...this.mapKitchenTunnelPricingFundingState(input),
    });
  }

  private async loadWorkbenchConfiguration(workbenchConfigurationId: string) {
    this.tunnelDataStore.updateConfigurationData(
      CONFIG_CONTEXT.WORKBENCH,
      await this.workbenchService.getProjectById(workbenchConfigurationId, true)
    );
  }

  public async loadKitchenPricing(tunnelConfigurationId: string) {
    try {
      await this.computeKitchenPricing({
        tunnelConfigurationId: tunnelConfigurationId,
        shouldRecalculate: false,
      });
    } catch (error) {
      logError(error);
    }
  }

  private loadDetailInstallation(result: KitchenInstallationDto) {
    this.tunnelDataStore.updateCurrentInstallation({
      ...this.currentInstallation,
      withInstallation: this.currentInstallation?.withInstallation ?? true,
      more2Years: result.houseMoreThan2YearsOld,
      detailInstallation: JSON.parse(result.installationPrice.installationDetails),
    });
  }

  private async initKitchenInspiration(configurationId: string): Promise<KitchenInspiration> {
    if (!configurationId) return undefined;

    let kitchenInspiration: KitchenInspiration = await this.getInspiration({
      tunnelConfigurationId: configurationId,
    });

    if (kitchenInspiration != null) {
      //remplacer par KitchenInspiration
      this.current.kitchen.styleId = kitchenInspiration?.styleId ?? this.current.kitchen.styleId;
      this.current.kitchen.modelId = kitchenInspiration?.modelId ?? this.current.kitchen.modelId;
      this.current.kitchen.layoutRef = kitchenInspiration?.layoutRef ?? this.current.kitchen.layoutRef;
      this.current.kitchen.fridgeConfigurationRef =
        kitchenInspiration?.fridgeConfigurationRef ?? this.current.kitchen.fridgeConfigurationRef;
      this.current.kitchen.ovenConfigurationRef =
        kitchenInspiration?.ovenConfigurationRef ?? this.current.kitchen.ovenConfigurationRef;
    }
    return kitchenInspiration;
  }

  public async getKitchenMedias(HBMConfigurationId: string): Promise<HBMMedias> {
    let kitchenMedias: HBMMedias = new HBMMedias();
    try {
      let projectData: any = await this.projectService.getDetails(HBMConfigurationId);
      projectData.medias.thumbnail.map(thumbnail => {
        let p: HBMMedia = {
          name: thumbnail.name,
          url: thumbnail.url,
        };
        kitchenMedias.thumbnail.push(p);
      });

      projectData.medias.plan2D.map(plan2D => {
        let p: HBMMedia = {
          name: plan2D.name,
          url: plan2D.url,
        };
        kitchenMedias.plan2D.push(p);
      });
    } catch (error) {
      logError(error);
    }
    return kitchenMedias;
  }

  public async createKitchen(initialData: KitchenConfigurationData) {
    initialData.inspiration =
      this.currentKitchenConfiguration?.inspiration ??
      ({
        styleId: null,
        modelId: null,
        layoutRef: null,
        ovenConfigurationRef: null,
        fridgeConfigurationRef: null,
      } as KitchenInspiration);

    const currentConfigurationId = this.currentKitchenConfiguration.configurationId;
    const currentKitchenConfigurationId = this.currentKitchenConfiguration.kitchenConfigurationId;

    let resultCreate = null;
    if (currentConfigurationId) {
      await this.updateKitchenConfiguration({
        ...initialData,
        configurationId: currentConfigurationId,
      });
    } else {
      resultCreate = await this.createKitchenTunnel({
        bom: initialData.bom,
        uuidhbm: initialData.HBMConfigurationId,
        kitchenInspiration: initialData.inspiration,
        title: initialData.projectTitle,
      });
    }

    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();

    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.KITCHEN, {
      ...initialData,
      configurationId: currentConfigurationId ?? resultCreate.id,
      kitchenConfigurationId: currentKitchenConfigurationId ?? resultCreate.kitchenConfigurationId,
    } as KitchenConfigurationData);
  }

  public async updateWithInstallation(withInstallation: boolean) {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    await this.updateInstallation({
      tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
      installationUpdate: {
        withInstallation: withInstallation,
      },
    });
  }

  public async updateKitchenWorkbenchConfigurationId(workbenchConfigurationId: string) {
    this.tunnelDataStore.resetCurrentInstallation();
    this.tunnelDataStore.resetCurrentPricing();
    this.tunnelDataStore.resetCurrentFunding();

    if (!this.currentKitchenConfiguration.workbenchConfigurationId) {
      await this.addWorkbench({
        workbenchConfigurationId: workbenchConfigurationId,
        tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
      });
    }

    await this.updateKitchenConfiguration({
      workbenchConfigurationId: workbenchConfigurationId,
    });

    await this.computePricing(true, false);
  }

  private async updatePricing(pricing: KitchenTunnelPricingDto) {
    if (pricing) {
      await this.updateKitchenPricingFromKitchenTunnelPricing(pricing);
    }
  }

  public async updateDiscount(input: ApplyDiscountInput) {
    this.tunnelDataStore.setPricingLoading(true);
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();
    const discountInput = {
      ...input,
      requiresFinancingRecalculation: financementEnabled && input.requiresFinancingRecalculation,
    };
    let res = await this.applyDiscount(discountInput);
    await this.updateKitchenPricingFromKitchenTunnelPricing(res);
    await this.computeFidelity();
    this.tunnelDataStore.updateCurrentDiscount(discountInput.discountType);
    this.tunnelDataStore.setPricingLoading(false);
  }

  public async updateKitchenConfiguration(data: KitchenConfigurationData, withReset: boolean = true) {
    data = {
      ...this.currentKitchenConfiguration,
      ...data,
    };
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.KITCHEN, data);
    data.inspiration = { ...data.inspiration };

    const inputRequest = {
      tunnelConfigurationId: data.configurationId,
      title: this.current.HBMProject.name,
      bom: data.bom,
      uuidhbm: data.HBMConfigurationId ?? this.current.HBMProject.uuid,
      kitchenInspiration: data.inspiration,
    };
    if (this.currentKitchenConfiguration.configurationId == null) {
      const resultCreate = await this.createKitchenTunnel(inputRequest);

      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.KITCHEN, {
        configurationId: resultCreate.id,
        kitchenConfigurationId: resultCreate.kitchenConfigurationId,
        projectTitle: this.current.HBMProject.name,
        inspiration: data.inspiration,
      } as KitchenConfigurationData);
    } else {
      await this.updateKitchenTunnel(inputRequest);
    }

    if (withReset) {
      this.tunnelDataStore.resetCurrentInstallation();
      this.tunnelDataStore.resetCurrentPricing();
      this.tunnelDataStore.resetCurrentFunding();
      this.tunnelDataStore.updateCurrentDiscount(DiscountTypeEnum.Discount0);
    }
  }

  private buildFidelityWithInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithInstallation?.total > 0
        ? this.currentFunding?.fundingWithInstallation?.total
        : this.currentPricing?.totalWithFunding + this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private buildFidelityWithoutInstallationInput(
    input: GetCustomerFidelityInfoNouveauPointInput
  ): GetCustomerFidelityInfoNouveauPointInput {
    let otherTotal =
      this.currentFunding?.fundingWithoutInstallation?.total > 0
        ? this.currentFunding?.fundingWithoutInstallation?.total
        : this.currentPricing?.totalWithFunding - this.currentPricing?.delta.actualAmount;
    return {
      ...input,
      amount: !this.currentInstallation?.withInstallation ? Math.floor(input.amount) : Math.floor(otherTotal),
    };
  }

  private async buildInstallationInput() {
    let input: InstallationInput = {
      configurationID: this.currentKitchenConfiguration.configurationId,
      workbenchConfigurationProducts: undefined,
    };

    if (this.currentKitchenConfiguration.workbenchConfigurationId) {
      input.workbenchConfigurationProducts = (await this.workbenchService.getResume()).products as WorkbenchEan[];
    }
    return input;
  }

  protected async fidelityEnabled(): Promise<boolean> {
    return this.featureSettingsService.isFidelityEnabled();
  }

  private async computeFidelity() {
    if (!(await this.fidelityEnabled())) return;
    if (!this.currentPricing?.totalWithoutFunding?.actualAmount) return;
    await this.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding?.actualAmount,
      customerId: this.currentCustomer.kfId,
    });
  }

  /** Calcule l'installation et met à jour le pricing. Retourne `false` si le calcul a échoué. */
  public async computeInstallation(): Promise<boolean> {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return true;
    }

    try {
      this.tunnelDataStore.setInstallationLoading(true);
      let result = await this.getInstallationData({
        tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
        // Pas de `?? false` : un âge inconnu ne doit rien imposer, sinon l'API persiste `false`
        // sur un projet dont l'âge d'habitation était `true`.
        houseMoreThan2YearsOld: this.currentInstallation?.more2Years,
      });
      this.loadDetailInstallation(result);
      this.loadPricingFromInstallation(result);
      return true;
    } catch (error) {
      logError(error);
      return false;
    } finally {
      this.tunnelDataStore.setInstallationLoading(false);
    }
  }

  private getDetailConception(input: KitchenInstallationDto) {
    if (input.withInstallation) {
      return new Map<string, Price>([
        ['totalNonAppliances', this.toPrice(input.conceptionPrices.withInstallation.productsNonAppliancesPrice)],
        ['totalAppliances', this.toPrice(input.conceptionPrices.withInstallation.productsAppliancesPrice)],
      ]);
    } else {
      return new Map<string, Price>([
        // Spread les entrées supplémentaires correctement
        ...Object.entries(input.conceptionPrices.categoryTotals).map(([key, value]): [string, Price] => [
          key,
          this.toPrice(value),
        ]),
      ]);
    }
  }

  private loadPricingFromInstallation(input: KitchenInstallationDto) {
    let withInstallationKey: string = input.withInstallation ? 'withInstallation' : 'withoutInstallation';
    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as KitchenTunnelConceptionDetailPrice).totalPrice
      ),
      delivery: this.toPrice(input.deliveryPrice),
      totalInstallation: this.toPrice(input.installationPrice.price),
      installationPriceRange: toInstallationPriceRange(input.installationPrice),
      totalWithoutFunding: this.toPrice(
        (input.conceptionPrices[withInstallationKey] as KitchenTunnelConceptionDetailPrice).totalPrice
      ),
      detailConception: this.getDetailConception(input),
      extraPricing: {
        totalWorkbench: this.toPrice(this.getTotalWorkbenchPricedItem(input.workbenchPrices)),
        totalInstallation: input.workbenchPrices.packagePrices
          ? this.toPrice(input.workbenchPrices.packagePrices.Installation)
          : undefined,
        totalDelivery: input.workbenchPrices.packagePrices
          ? this.toPrice(input.workbenchPrices.packagePrices.Transport)
          : undefined,
        totalWorkbenchPlan: this.toPrice(input.workbenchPrices.workbenchPlanPrice),
        HBMWorkbench: this.toPrice(input.workbenchPrices.workbench3DPrice),
        kitchenWorkbenchConfigurations: input.workbenchPrices.workbenchProducts,
      } as KitchenExtraPricing,
      delta: this.toPrice(input.conceptionPrices.delta),
      totalComplementaryProduct: this.toPrice(input.complementaryProductPrice),
    });
  }

  public async computeFidelityPoint(input: GetCustomerFidelityInfoNouveauPointInput) {
    if (!(await this.fidelityEnabled())) return;
    try {
      this.tunnelDataStore.setFidelityLoading(true);

      const [withInstallation, withoutInstallation] = await Promise.all([
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithInstallationInput(input)),
        this.fidelityService.getCustomerFidelityCardInfoNouveauPoint(this.buildFidelityWithoutInstallationInput(input)),
      ]);

      this.tunnelDataStore.updateCurrentPricing({
        ...this.currentPricing,
        fidelity: [withInstallation, withoutInstallation],
      });
    } catch (e) {
      logError(e);
    } finally {
      this.tunnelDataStore.setFidelityLoading(false);
    }
  }

  public async computePricing(shouldRecalculate = true, computeInstallation = true) {
    try {
      await this.computeKitchenPricing(
        {
          tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
          shouldRecalculate: shouldRecalculate,
        },
        computeInstallation
      );
    } catch (error) {
      logError(error);
    }
  }

  private updateKitchenInstallation(result: KitchenTunnelPricingDto) {
    let detailInstallation = null;
    if (result.installationPrices?.installationDetails != undefined) {
      detailInstallation = JSON.parse(result.installationPrices?.installationDetails);
    }

    this.tunnelDataStore.updateCurrentInstallation({
      more2Years: result.houseMoreThan2YearsOld,
      withInstallation: result.withInstallation,
      detailInstallation: detailInstallation,
    });
  }

  private computeLitekitchenPricing(input: KitchenTunnelPricingDto) {
    this.tunnelDataStore.updateCurrentPricing({
      totalComplementaryProduct: this.toPrice(input.complementaryProductPrice),
    });
  }

  private async updateKitchenPricingFromKitchenTunnelPricing(
    input: KitchenTunnelPricingDto,
    computeInstallation: boolean = true
  ) {
    const featureSettings = await this.featureSettingsService.getSettings();

    if (computeInstallation && featureSettings.pose) {
      this.updateKitchenInstallation(input);
    } else if (!featureSettings.pose) {
      this.tunnelDataStore.disableCurrentInstallation();
    }

    if (!input.conceptionPrices) {
      this.computeLitekitchenPricing(input);
    } else if (featureSettings.pose && this.currentInstallation?.withInstallation) {
      this.computeKitchenPricingWithInstallation(input);
    } else {
      this.computeKitchenPricingWithoutInstallation(input);
    }

    if (featureSettings.financement) {
      await this.loadKitchenFunding(input);
    } else {
      this.tunnelDataStore.disableCurrentFunding();
    }

    if (typeof input.discountEnum === 'number') {
      this.tunnelDataStore.updateCurrentDiscount(this.discountValueToDiscountEnum(input.discountEnum as number));
    } else {
      this.tunnelDataStore.updateCurrentDiscount(input.discountEnum);
    }
  }

  private async computeKitchenPricing(input: GetPricingInput, computeInstallation: boolean = true) {
    try {
      this.tunnelDataStore.setPricingLoading(true);
      let result: KitchenTunnelPricingDto = await this.getPricing(input);
      await this.updateKitchenPricingFromKitchenTunnelPricing(result, computeInstallation);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setPricingLoading(false);
    }
  }

  public async computeFunding(input: GetFundingDataInput) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      // Financement coupé : on recharge quand même le pricing pour que les totaux suivent
      // le scénario de pose courant (le mapper de pricing repasse par disableCurrentFunding).
      this.tunnelDataStore.disableCurrentFunding();
      await this.loadKitchenPricing(input.tunnelConfigurationId);
      return;
    }

    try {
      this.tunnelDataStore.setFundingLoading(true);
      let result = await this.getFundingData(input);
      await this.updatePricing(result);
    } catch (error) {
      logError(error);
    } finally {
      this.tunnelDataStore.setFundingLoading(false);
    }
  }

  public async computeComplementaryProduct(
    input: GetComplementaryProductsInput,
    shouldRecalculateFunding: boolean = true
  ) {
    try {
      this.tunnelDataStore.setComplementaryProductLoading(true);
      const financementEnabled = await this.featureSettingsService.isFinancementEnabled();
      let result = await this.getComplementaryProducts(input);
      await this.updateComplementaryProducts(result);
      this.computePricing(shouldRecalculateFunding && financementEnabled, false);
    } catch (error) {
      console.error(error);
    } finally {
      this.tunnelDataStore.setComplementaryProductLoading(false);
    }
  }

  private async updateComplementaryProducts(result: TunnelComplementaryProduct) {
    const disabledWithQuantity = result.products.filter(p => p.isDisabled && p.quantity > 0);
    const hasRemovedProducts = disabledWithQuantity.length > 0;
    const filteredProducts = result.products.filter(p => !p.isDisabled);

    // Si des produits désactivés avaient une quantité > 0, on met à zéro leurs commandes côté backend
    // pour que le recalcul du prix ne les inclue plus
    if (hasRemovedProducts) {
      const configurationId = this.currentKitchenConfiguration?.kitchenConfigurationId;
      if (configurationId) {
        const productsToZero = disabledWithQuantity.map(p => ({ ...p, quantity: 0 }));
        await this.complementaryProductOrderService.upsertListComplementaryProductOrders(
          configurationId,
          productsToZero
        );
      }
    }

    this.tunnelDataStore.updateCurrentComplementaryProduct({
      ...result,
      products: filteredProducts,
      hasRemovedProducts: hasRemovedProducts
    } as any);
  }

  private getTotalWorkbenchPricedItem(input: KitchenTunnelWorkbenchPrice) {
    if (input.workbenchPlanPrice.fullPrice > 0) {
      return this.addPricedItem([
        input.workbenchPlanPrice,
        input.packagePrices.Transport,
        input.packagePrices.Installation,
      ]);
    }
  }

  private computeKitchenPricingWithoutInstallation(result: KitchenTunnelPricingDto) {
    const installationDisabled = !this.featureSettingsService.poseEnabled;
    const totalWithoutFunding = installationDisabled
      ? result.conceptionPrices?.withoutInstallation?.totalPrice ?? result.totalWithoutFinancing
      : result.totalWithoutFinancing;

    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(
        this.addPricedItem([
          result.conceptionPrices.withoutInstallation?.productsAppliancesPrice,
          result.conceptionPrices.withoutInstallation?.productsNonAppliancesPrice,
        ])
      ),
      delivery: this.toPrice(result.deliveryPrice),
      totalWithoutFunding: this.toPrice(totalWithoutFunding),
      detailConception: new Map<string, Price>(
        Object.entries(result.conceptionPrices.categoryTotals ?? {}).map(([key, value]): [string, Price] => [
          key,
          this.toPrice(value),
        ])
      ),

      totalComplementaryProduct: this.toPrice(result.complementaryProductPrice),
      extraPricing: {
        totalWorkbench: this.toPrice(this.getTotalWorkbenchPricedItem(result.workbenchPrices)),
        totalInstallation: result.workbenchPrices.packagePrices
          ? this.toPrice(result.workbenchPrices.packagePrices.Installation)
          : undefined,
        totalDelivery: result.workbenchPrices.packagePrices
          ? this.toPrice(result.workbenchPrices.packagePrices.Transport)
          : undefined,
        totalWorkbenchPlan: this.toPrice(result.workbenchPrices.workbenchPlanPrice),
        HBMWorkbench: this.toPrice(result.workbenchPrices.workbench3DPrice),
        kitchenWorkbenchConfigurations: result.workbenchPrices.workbenchProducts,
      } as KitchenExtraPricing,
      totalInstallation: installationDisabled ? this.toPrice(0) : undefined,
      delta: installationDisabled ? this.toPrice(0) : this.toPrice(result.conceptionPrices.delta),
      totalWithFunding: result.totalWithFinancing,
    });
  }

  private computeKitchenPricingWithInstallation(result: KitchenTunnelPricingDto) {
    this.tunnelDataStore.updateCurrentPricing({
      totalConception: this.toPrice(
        this.addPricedItem([
          result.conceptionPrices.withInstallation.productsAppliancesPrice,
          result.conceptionPrices.withInstallation.productsNonAppliancesPrice,
        ])
      ),
      delivery: this.toPrice(result.deliveryPrice),
      totalInstallation: this.toPrice(result.installationPrices.price),
      installationPriceRange: toInstallationPriceRange(result.installationPrices),
      totalWithoutFunding: this.toPrice(result.totalWithoutFinancing),
      detailConception: new Map<string, Price>([
        ['totalNonAppliances', this.toPrice(result.conceptionPrices.withInstallation.productsNonAppliancesPrice)],
        ['totalAppliances', this.toPrice(result.conceptionPrices.withInstallation.productsAppliancesPrice)],
      ]),
      totalComplementaryProduct: this.toPrice(result.complementaryProductPrice),
      extraPricing: {
        totalWorkbench: this.toPrice(this.getTotalWorkbenchPricedItem(result.workbenchPrices)),
        totalInstallation: result.workbenchPrices.packagePrices
          ? this.toPrice(result.workbenchPrices.packagePrices.Installation)
          : undefined,
        totalDelivery: result.workbenchPrices.packagePrices
          ? this.toPrice(result.workbenchPrices.packagePrices.Transport)
          : undefined,
        totalWorkbenchPlan: this.toPrice(result.workbenchPrices.workbenchPlanPrice),
        HBMWorkbench: this.toPrice(result.workbenchPrices.workbench3DPrice),
        kitchenWorkbenchConfigurations: result.workbenchPrices.workbenchProducts,
      } as KitchenExtraPricing,
      delta: this.toPrice(result.conceptionPrices.delta),
      totalWithFunding: result.totalWithFinancing,
    });
  }

  public async selectOffer(code: string, duration: number) {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    await this.getFundingLegalInformation({
      tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
      selectedBareme: code,
      financingDuration: duration,
    });
    await this.loadKitchenPricing(this.currentKitchenConfiguration.configurationId);
  }

  private mapKitchenTunnelPricingFundingState(input: KitchenTunnelPricingDto): FundingState {
    return {
      downPayment: input.downPayment,
      fundingWithInstallation: this.buildKitchenFundingFromDto(input.financingWithInstallation),
      fundingWithoutInstallation: this.buildKitchenFundingFromDto(input.financing),
    };
  }

  /**
   * Construit un {@link Funding} à partir d'un DTO de financement persisté.
   * Replie sur un funding vide quand le scénario est absent — le back renvoie `null` (pas
   * `undefined`) pour le scénario non retenu, d'où le garde sur `offers` plutôt que sur l'objet.
   */
  private buildKitchenFundingFromDto(financing?: BaseTunnelFundingDto): Funding {
    if (!financing?.offers) {
      return this.buildEmptyFunding();
    }

    const offerTable = this.mapRawOffersLiteOfferResults(JSON.parse(financing.offers));
    return {
      code: financing.select_bareme,
      duration: financing.select_duration,
      offerTable,
      ...this.getDataFromFundingOfferTable(financing.select_bareme, financing.select_duration, offerTable),
    };
  }

  private updateFunding(input: {
    fundingWithoutInstallation: Funding;
    fundingWithInstallation: Funding;
    downPayment?: number;
  }) {
    this.tunnelDataStore.updateCurrentFunding({
      ...this.currentFunding,
      ...(input.downPayment !== undefined && { downPayment: input.downPayment }),
      fundingWithInstallation: input.fundingWithInstallation,
      fundingWithoutInstallation: input.fundingWithoutInstallation,
    });
  }

  private getFunding(other: boolean) {
    const shouldUseFundingWithoutInstallation = other
      ? this.currentInstallation.withInstallation
      : !this.currentInstallation.withInstallation;
    return this.currentFunding[
      shouldUseFundingWithoutInstallation ? 'fundingWithoutInstallation' : 'fundingWithInstallation'
    ];
  }

  public async deleteKitchen(input: DeleteTunnelInput) {
    return await this.deleteTunnel(input);
  }

  public async duplicateKitchen(input: DuplicateTunnelHBMInput) {
    return await this.duplicateHbmTunnel(input);
  }

  public async generateKitchenPdf(input: HbmPdfViewModel) {
    return await this.getKitchenPdf(input);
  }

  private createKitchenTunnel(input: KitchenCreateUpdateTunnel): Promise<KitchenConfiguration> {
    return this.postHbm<KitchenCreateUpdateTunnel, KitchenConfiguration>(this.apiUrl + 'Tunnel/create', input);
  }

  private updateKitchenTunnel(input: KitchenCreateUpdateTunnel): Promise<KitchenConfiguration> {
    return this.putHbm<KitchenCreateUpdateTunnel, KitchenConfiguration>(
      this.apiUrl + 'Tunnel/update/' + input.tunnelConfigurationId,
      input
    );
  }

  private getKitchenTunnelByUUID(input: GetTunnelByHBMUUIDInput): Promise<BaseKitchenConfigurationDto> {
    return this.getHbm<BaseKitchenConfigurationDto>(this.apiUrl + 'Tunnel/hbm/' + input.hbmUUID);
  }

  private getInstallationData(input: GetInstallationDataInput): Promise<KitchenInstallationDto> {
    return this.postHbm<{ houseMoreThan2YearsOld: boolean }, KitchenInstallationDto>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      undefined,
      { houseMoreThan2YearsOld: input.houseMoreThan2YearsOld }
    );
  }

  //quand tu change le withInstallation
  private updateInstallation(input: UpdateInstallationInput): Promise<void> {
    return this.putHbm<TunnelUpdateInstallation, void>(
      this.apiUrl + 'Tunnel/installation/' + input.tunnelConfigurationId,
      input.installationUpdate
    );
  }

  private getComplementaryProducts(input: GetComplementaryProductsInput): Promise<TunnelComplementaryProduct> {
    return this.get<TunnelComplementaryProduct>(
      this.apiUrl + 'Tunnel/complementaryProducts',
      this.buildHttpParamsFromObject({
        tunnelConfigurationId: input.tunnelConfigurationId,
      })
    );
  }

  //quand tu change le discount
  private applyDiscount(input: ApplyDiscountInput): Promise<KitchenTunnelPricingDto> {
    return this.postHbm<ApplyDiscountInput, KitchenTunnelPricingDto>(
      this.apiUrl + 'Tunnel/discount/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getPricing(input: GetPricingInput): Promise<KitchenTunnelPricingDto> {
    return this.postHbm<GetPricingInput, KitchenTunnelPricingDto>(
      this.apiUrl + 'Tunnel/pricing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getFundingData(input: GetFundingDataInput): Promise<KitchenTunnelPricingDto> {
    return this.postHbm<GetFundingDataInput, KitchenTunnelPricingDto>(
      this.apiUrl + 'Tunnel/financing/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getInspiration(input: GetInspirationInput): Promise<KitchenInspirationDto> {
    return this.postHbm<GetInspirationInput, KitchenInspirationDto>(
      this.apiUrl + 'Tunnel/inspiration/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private updateInspiration(input: UpdateInspirationInput): Promise<void> {
    return this.putHbm<KitchenInspirationDto, void>(
      this.apiUrl + 'Tunnel/inspiration/' + input.tunnelConfigurationId,
      input.inspirationData
    );
  }

  private addWorkbench(input: AddWorkbenchInput): Promise<BaseKitchenConfigurationDto> {
    return this.postHbm<AddWorkbenchInput, BaseKitchenConfigurationDto>(
      this.apiUrl + 'Tunnel/workbench/' + input.tunnelConfigurationId,
      undefined,
      input
    );
  }

  private getKitchenPdf(input: HbmPdfViewModel) {
    return this.postHbm<HbmPdfViewModel, any>(this.baseApiUrl + 'Pdf/GeneratePdf/kitchen/', input, {
      tunnelConfigurationId: input.tunnelConfigurationId,
    });
  }

  private getFundingLegalInformation(input: GetFundingLegalInformationInput): Promise<FundingLegalInformation> {
    return this.getHbm<FundingLegalInformation>(
      this.apiUrl + 'Tunnel/financing/legal-information/' + input.tunnelConfigurationId,
      input
    );
  }

  public async getKitchenComplementaryProductList(): Promise<ComplementaryProduct[]> {
    const products = await this.get<ComplementaryProduct[]>(this.apiUrl + 'ComplementaryProduct/list');
    return products
      .filter(p => !p.isDisabled)
      .map(p => ({
        ...p,
        quantity: 0,
      }));
  }

  private duplicateHbmTunnel(input: DuplicateTunnelHBMInput): Promise<BaseKitchenConfigurationDto> {
    return this.postHbm<DuplicateTunnelHBMInput, BaseKitchenConfigurationDto>(
      this.apiUrl + 'Tunnel/duplicate/hbm',
      undefined,
      input
    );
  }

  private deleteTunnel(input: DeleteTunnelInput): Promise<BaseKitchenConfigurationDto> {
    return this.deleteHbm<DuplicateTunnelHBMInput, BaseKitchenConfigurationDto>(
      this.apiUrl + 'Tunnel/delete/' + input.tunnelConfigurationId
    );
  }

  postInstallation(input: InstallationInput): Promise<KitchenPricing> {
    return this.post(this.apiUrl + 'spid/GetInstallationData', input);
  }

  override duplicateProjectById(id: string, title: string): Promise<any> {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(`${this.apiUrl}configuration/duplicate`, {}, params);
  }

  updateProjectUUID(configurationId: string, newProjectUUID: string) {
    const params = new HttpParams().set('configurationId', configurationId).set('newProjectUUID', newProjectUUID);
    return this.post(`${this.apiUrl}configuration/updateProjectUUID`, {}, params);
  }

  getConfigurationByProjectUUID(input: any): Promise<any> {
    return this.post(this.apiUrl + 'configuration/project', input);
  }

  public async getInstallationGeneric() {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return undefined;
    }

    return this.getInstallationData({
      tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
    });
  }

  //quand tu change les installations générique
  public async updateInstallationGeneric(installationGeneric: any) {
    const poseEnabled = await this.featureSettingsService.isPoseEnabled();

    if (!poseEnabled) {
      this.tunnelDataStore.disableCurrentInstallation();
      return;
    }

    return this.updateInstallationGenericData({
      tunnelConfigurationId: this.currentKitchenConfiguration.configurationId,
      installationGeneric: installationGeneric,
    });
  }

  private updateInstallationGenericData(input: any): Promise<void> {
    return this.putHbm<TunnelUpdateInstallation, void>(
      this.apiUrl + 'Tunnel/installationGeneric/' + input.tunnelConfigurationId,
      {
        withInstallation: this.currentInstallation.withInstallation,
        installationGeneric: input.installationGeneric,
      }
    );
  }
}
